import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, Layers3 } from 'lucide-react';
import { Input } from '../components/ui/input';
import BackButton from '../components/BackButton';

function VariantListPage() {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleDeleteVariant = async (variantId, variantName) => {
    const confirmed = window.confirm(
      `Delete variant "${variantName}"? This will remove its generated pages and images.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/variants/${variantId}`);
      setVariants((prev) => prev.filter((variant) => variant._id !== variantId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete variant.');
    }
  };

  useEffect(() => {
    const loadVariants = async () => {
      try {
        const booksResponse = await axios.get('/api/list_books');
        const books = booksResponse.data || [];

        const variantResponses = await Promise.all(
          books.map(async (book) => {
            const response = await axios.get(`/api/books/${book._id}/variants`);
            return (response.data || []).map((variant) => ({
              ...variant,
              source_book_id: book._id,
              source_book_name: book.name,
            }));
          }),
        );

        setVariants(variantResponses.flat());
      } catch (err) {
        setError('Failed to load variants.');
      } finally {
        setLoading(false);
      }
    };

    loadVariants();
  }, []);

  const filteredVariants = variants.filter((variant) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return true;
    }

    return (
      variant.name?.toLowerCase().includes(query) ||
      variant.source_book_name?.toLowerCase().includes(query) ||
      variant.target_format_label?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-16">
        <p className="text-gray-600">Loading variants...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6">
      <BackButton className="absolute" />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Variants</h1>
            <p className="mt-2 text-gray-600">
              Browse and manage variants across all books.
            </p>
          </div>
          <span className="text-sm text-gray-500">{filteredVariants.length} shown</span>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          <Input
            type="text"
            placeholder="Search variants by name, book, or format..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Existing Variants</h2>
          <span className="text-sm text-gray-500">{variants.length} total</span>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {variants.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <Layers3 className="mx-auto mb-4 h-10 w-10 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">
              No variants yet
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Create a variant from a book&apos;s edit screen using `Open Variant
              Tools`.
            </p>
            <Link
              to="/books"
              className="mt-4 inline-flex rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Go to My Books
            </Link>
          </div>
        ) : filteredVariants.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No variants found matching &quot;{searchTerm}&quot;
          </div>
        ) : (
          <div className="space-y-3">
            {filteredVariants.map((variant) => (
              <div
                key={variant._id}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
              >
                <div>
                  <div className="font-medium text-gray-900">{variant.name}</div>
                  <div className="text-sm text-gray-500">
                    Source book: {variant.source_book_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {variant.target_format_label} • {variant.resize_mode}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/variants/${variant._id}/pages`}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Open
                  </Link>
                  <Link
                    to={`/books/${variant.source_book_id}/variants/new`}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Variant Tools
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDeleteVariant(variant._id, variant.name)}
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VariantListPage;
